/** Liste + éditeur des factures récurrentes. */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { facturxFetch } from "../api/client";
import { useHubBack } from "../mobile/hubBack";
import { Button } from "../components/Button";
import { ConfirmModal } from "../components/ConfirmModal";
import { PageHeader } from "../components/PageHeader";
import { ListPager, usePagedRows } from "../components/ListPager";
import { SortTh, sortRows, useTableSort } from "../components/tableSort";
import { RecurringEditor } from "../features/recurring/RecurringEditor";
import { RecurringMenu } from "../features/recurring/RecurringMenu";
import {
  asRecFreq,
  recCyclesLabel,
  recDueDays,
  recDueOn,
  recFreqLabel,
  recStatusLabel,
  recStatusTone,
  type Recurring,
  type RecurringDraft,
} from "../features/recurring/types";
import { QuotePdfDrawer } from "../features/quotes/QuotePdfDrawer";
import { printQuotePdf, quotePrintUrl } from "../features/quotes/quotePdf";
import { QuotesListBar } from "../features/quotes/QuotesListBar";
import { localDay, REC_FILTERS } from "../features/quotes/docFilters";
import { amountMatches } from "../features/quotes/format";
import { draftToPayload, emptyDraft, lineKeptWithoutLabel, quoteToDraft, todayIso, addDays, type PaymentTerm, type Quote } from "../features/quotes/types";
import type { ClientListItem } from "../features/clients/types";
import type { TaxRate } from "../features/catalog/types";
import { formatsFromCompany } from "../formats/types";
import { useFormatters } from "../formats/useFormatters";
import { useCompany } from "../company/CompanyProvider";
import "../features/clients/tables.css";

export type RecurringSeed = { clientId?: number; fromDocumentId?: number; recurringId?: number; filter?: string };

function listOnlyFilter(seed: RecurringSeed | null | undefined): string | null {
  if (!seed?.filter || seed.recurringId || seed.fromDocumentId || seed.clientId) return null;
  return seed.filter;
}

type RecurringPageProps = {
  companyId: number;
  seed?: RecurringSeed | null;
  onSeedUsed?: () => void;
  onOpenClient?: (clientId: number) => void;
  onOpenInvoice?: (id: number) => void;
};

function asRec(row: Recurring): Recurring {
  return {
    ...row,
    id: Number(row.id),
    company_id: Number(row.company_id),
    client_id: Number(row.client_id),
    document_id: Number(row.document_id),
    number: row.number || null,
    frequency: asRecFreq(row.frequency),
    cycles_left: row.cycles_left === null || row.cycles_left === undefined ? null : Number(row.cycles_left),
    total_ttc_cents: Number(row.total_ttc_cents) || 0,
    due_days: row.due_days == null ? row.due_days : Number(row.due_days) === 0 ? 0 : recDueDays(row.due_days),
    waiting: row.waiting === true,
  };
}

function termIdForDays(terms: PaymentTerm[], days: number, keepId?: number | null): number | null {
  const n = recDueDays(days);
  if (keepId && terms.some((t) => t.id === keepId && Number(t.days) === n && !t.end_of_month)) {
    return keepId;
  }
  const hit = terms.find((t) => Number(t.days) === n && !t.end_of_month);
  return hit ? hit.id : null;
}

function daysFromTerm(terms: PaymentTerm[], termId: number | null | undefined, fallback = 30): number {
  const hit = terms.find((t) => t.id === termId);
  return recDueDays(hit ? hit.days : fallback);
}

export function RecurringPage({ companyId, seed, onSeedUsed, onOpenClient, onOpenInvoice }: RecurringPageProps) {
  const { company } = useCompany();
  const { money, day } = useFormatters();
  const decimalComma = formatsFromCompany(company).decimal_comma;
  const legal = { terms_html: company?.invoice_terms || "", footer_html: company?.invoice_footer || "" };
  const [rows, setRows] = useState<Recurring[]>([]);
  const [clients, setClients] = useState<ClientListItem[]>([]);
  const [rates, setRates] = useState<TaxRate[]>([]);
  const [terms, setTerms] = useState<PaymentTerm[]>([]);
  const [defaultTaxId, setDefaultTaxId] = useState<number | null>(null);
  const [q, setQ] = useState("");
  const seedRef = useRef(seed);
  seedRef.current = seed;
  const [filter, setFilter] = useState(() => listOnlyFilter(seed) || "all");
  const [view, setView] = useState<"list" | "form">("list");
  const [draft, setDraft] = useState<RecurringDraft | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [menuId, setMenuId] = useState<number | null>(null);
  const [confirm, setConfirm] = useState<{ kind: "del" | "end"; id: number } | null>(null);
  const [pdf, setPdf] = useState<{ id: number; number: string | null } | null>(null);
  useHubBack((view === "form" ? 1 : 0) + (confirm || pdf ? 1 : 0), () => {
    if (pdf) {
      setPdf(null);
      return;
    }
    if (confirm) {
      setConfirm(null);
      return;
    }
    setError("");
    setDraft(null);
    setView("list");
  });
  const sort = useTableSort<"status" | "number" | "client" | "freq" | "start" | "next" | "due" | "cycles" | "ttc">();

  const visible = useMemo(() => {
    const s = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter === "draft") {
        if (r.status !== "draft") return false;
      } else if (filter === "waiting") {
        if (!r.waiting) return false;
      } else if (filter === "running") {
        if (r.status !== "running" || r.waiting) return false;
      } else if (filter === "soon") {
        const next = (r.next_run_on || "").slice(0, 10);
        if (r.status !== "running" || !next || next > localDay(30)) return false;
      } else if (filter !== "all" && r.status !== filter) return false;
      if (!s) return true;
      return [r.number, r.client_name, recFreqLabel(r.frequency), recStatusLabel(r.status, r.waiting), day(recDueOn(r)), recDueDays(r.due_days) === 0 ? "à réception" : ""].some((v) =>
        (v || "").toLowerCase().includes(s),
      ) || amountMatches(r.total_ttc_cents, q) || amountMatches(r.total_ht_cents, q);
    });
  }, [rows, q, filter, day]);

  const shown = useMemo(() => {
    if (!sort.key) return visible;
    const kind = sort.key === "start" || sort.key === "next" || sort.key === "due" ? "date" : sort.key === "ttc" || sort.key === "cycles" ? "num" : "text";
    return sortRows(visible, sort.dir, kind, (r) => {
      if (sort.key === "status") return recStatusLabel(r.status, r.waiting);
      if (sort.key === "number") return r.number || "";
      if (sort.key === "client") return r.client_name || "";
      if (sort.key === "freq") return recFreqLabel(r.frequency);
      if (sort.key === "start") return r.starts_on || "";
      if (sort.key === "next") return r.status === "ended" ? "" : r.next_run_on || "";
      if (sort.key === "due") return recDueDays(r.due_days) === 0 ? "" : recDueOn(r) || "";
      if (sort.key === "cycles") return r.cycles_left == null ? Number.POSITIVE_INFINITY : r.cycles_left;
      return r.total_ttc_cents;
    });
  }, [visible, sort.key, sort.dir]);
  const paged = usePagedRows(shown);

  const load = useCallback(async () => {
    const data = await facturxFetch<{ recurring: Recurring[] }>(`recurring.php?company_id=${companyId}`);
    return (data.recurring || []).map(asRec);
  }, [companyId]);

  async function refresh() {
    setRows(await load());
  }

  useEffect(() => {
    setView("list");
    setDraft(null);
    setQ("");
    setFilter(listOnlyFilter(seedRef.current) || "all");
  }, [companyId]);

  useEffect(() => {
    let stop = false;
    (async () => {
      try {
        const [tax, cl, list] = await Promise.all([
          facturxFetch<{ tax_rates: TaxRate[]; default_tax_rate_id: number | null }>(`tax-rates.php?company_id=${companyId}`),
          facturxFetch<{ clients: ClientListItem[] }>(`clients.php?company_id=${companyId}`),
          load(),
        ]);
        if (stop) return;
        const lu = await facturxFetch<{ payment_terms: PaymentTerm[] }>(`lookups.php?company_id=${companyId}`);
        if (stop) return;
        setRates((tax.tax_rates || []).map((r) => ({ ...r, id: Number(r.id), rate_bp: Number(r.rate_bp) })));
        setDefaultTaxId(tax.default_tax_rate_id === null ? null : Number(tax.default_tax_rate_id));
        setClients(cl.clients || []);
        setTerms((lu.payment_terms || []).map((t) => ({ ...t, id: Number(t.id), days: Number(t.days) })));
        setRows(list);
      } catch (e) {
        if (!stop) setError(e instanceof Error ? e.message : "Erreur");
      }
    })();
    return () => {
      stop = true;
    };
  }, [companyId, load]);

  useEffect(() => {
    if (!seed) return;
    const only = listOnlyFilter(seed);
    if (only) {
      setFilter(only);
      setView("list");
      setDraft(null);
      onSeedUsed?.();
      return;
    }
    void startSeed(seed);
    onSeedUsed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed]);

  function blank(): RecurringDraft {
    return {
      client_id: 0,
      frequency: "monthly",
      starts_on: todayIso(),
      cycles_left: null,
      status: "draft",
      payment_term_id: company?.default_payment_term_id ?? null,
      payment_term_label: "",
      due_days: daysFromTerm(terms, company?.default_payment_term_id ?? null, 30),
      quote: emptyDraft(30, defaultTaxId, decimalComma, legal),
    };
  }

  function toDraft(row: Recurring): RecurringDraft {
    const doc = row.document as Quote | undefined;
    return {
      id: row.id,
      number: row.number || null,
      client_id: row.client_id,
      frequency: row.frequency,
      starts_on: row.starts_on,
      cycles_left: row.cycles_left,
      status: row.status,
      waiting: row.waiting === true,
      payment_term_id: row.payment_term_id,
      payment_term_label: row.payment_term_label || "",
      due_days: recDueDays(row.due_days ?? daysFromTerm(terms, row.payment_term_id, 30)),
      next_run_on: row.next_run_on,
      quote: doc
        ? quoteToDraft(doc, decimalComma, defaultTaxId, legal)
        : emptyDraft(30, defaultTaxId, decimalComma, legal),
    };
  }

  async function startSeed(s: RecurringSeed) {
    if (listOnlyFilter(s)) return;
    if (s.recurringId) {
      await openEdit(s.recurringId);
      return;
    }
    if (s.fromDocumentId) {
      setBusy(true);
      setError("");
      try {
        const data = await facturxFetch<{ recurring: Recurring }>("recurring.php", {
          method: "POST",
          body: JSON.stringify({ company_id: companyId, from_document_id: s.fromDocumentId, frequency: "monthly", starts_on: todayIso() }),
        });
        setDraft(toDraft(asRec(data.recurring)));
        setView("form");
        await refresh();
      } catch (e) {
        setError(e instanceof Error ? e.message : "Erreur");
      } finally {
        setBusy(false);
      }
      return;
    }
    const b = blank();
    if (s.clientId) {
      b.client_id = s.clientId;
      b.quote.client_id = s.clientId;
    }
    setDraft(b);
    setView("form");
  }

  async function openEdit(id: number) {
    setBusy(true);
    setError("");
    try {
      const data = await facturxFetch<{ recurring: Recurring }>(`recurring.php?id=${id}`);
      setDraft(toDraft(asRec(data.recurring)));
      setView("form");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  async function duplicateRecurring(id: number) {
    setBusy(true);
    setError("");
    try {
      const src =
        draft?.id === id
          ? draft
          : toDraft(asRec((await facturxFetch<{ recurring: Recurring }>(`recurring.php?id=${id}`)).recurring));
      const quote = { ...src.quote, client_id: 0 };
      delete quote.id;
      quote.lines = quote.lines.map((l) => ({ ...l, key: `l-${Math.random().toString(36).slice(2, 9)}` }));
      setDraft({
        client_id: 0,
        frequency: src.frequency,
        starts_on: src.starts_on,
        cycles_left: src.cycles_left,
        status: "draft",
        payment_term_id: src.payment_term_id,
        payment_term_label: src.payment_term_label,
        due_days: src.due_days,
        next_run_on: src.starts_on,
        quote,
      });
      setView("form");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  async function cloneAsInvoice(documentId: number, dueDays: number, paymentTermId: number | null) {
    if (!documentId) return;
    setBusy(true);
    setError("");
    try {
      const data = await facturxFetch<{ document: Quote }>(`documents.php?id=${documentId}`);
      const d = quoteToDraft(data.document, decimalComma, defaultTaxId, legal);
      delete d.id;
      d.status = "draft";
      d.number = null;
      d.immutable = false;
      d.signature = null;
      d.converted_invoice_id = null;
      d.paid = false;
      d.paid_cents = 0;
      d.issue_date = todayIso();
      d.operation_date = d.issue_date;
      d.due_date = addDays(d.issue_date, recDueDays(dueDays));
      const created = await facturxFetch<{ document: Quote }>("documents.php", {
        method: "POST",
        body: JSON.stringify({
          ...draftToPayload(d, companyId, decimalComma, "invoice"),
          payment_term_id: paymentTermId,
        }),
      });
      const newId = Number(created.document.id);
      if (newId && onOpenInvoice) onOpenInvoice(newId);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  function payload() {
    if (!draft) return null;
    const body = draftToPayload(draft.quote, companyId, decimalComma, "invoice");
    return {
      company_id: companyId,
      client_id: draft.client_id,
      frequency: draft.frequency,
      starts_on: draft.starts_on,
      cycles_left: draft.cycles_left,
      payment_term_id: termIdForDays(terms, draft.due_days, draft.payment_term_id),
      due_days: recDueDays(draft.due_days),
      notes: body.notes,
      terms_html: body.terms_html,
      footer_html: body.footer_html,
      discount_bp: body.discount_bp,
      discount_cents: body.discount_cents,
      lines: body.lines,
    };
  }

  async function save() {
    if (!draft) return;
    if (!draft.client_id) {
      setError("Le client est obligatoire.");
      return;
    }
    if (draft.quote.lines.some((l) => lineKeptWithoutLabel(l, decimalComma))) {
      setError("Chaque ligne doit avoir une désignation.");
      return;
    }
    const body = payload();
    if (!body) return;
    if (!body.payment_term_id) {
      setError("Choisissez une échéance.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const data = draft.id
        ? await facturxFetch<{ recurring: Recurring }>(`recurring.php?id=${draft.id}`, { method: "PATCH", body: JSON.stringify(body) })
        : await facturxFetch<{ recurring: Recurring }>("recurring.php", { method: "POST", body: JSON.stringify(body) });
      setDraft(toDraft(asRec(data.recurring)));
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  async function act(id: number, action: "launch" | "pause" | "end") {
    setBusy(true);
    setError("");
    try {
      const data = await facturxFetch<{ recurring: Recurring; invoice_id?: number }>(`recurring.php?id=${id}&action=${action}`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      const rec = asRec(data.recurring);
      if (draft?.id === id) setDraft(toDraft(rec));
      await refresh();
      if (action === "launch" && data.invoice_id && onOpenInvoice) {
        onOpenInvoice(data.invoice_id);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  async function applyConfirm() {
    if (!confirm) return;
    setBusy(true);
    setError("");
    try {
      if (confirm.kind === "del") {
        await facturxFetch(`recurring.php?id=${confirm.id}`, { method: "DELETE" });
        setConfirm(null);
        setDraft(null);
        setView("list");
      } else {
        await act(confirm.id, "end");
        setConfirm(null);
      }
      await refresh();
    } catch (e) {
      setConfirm(null);
      setError(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="w-full">
      {error && view !== "form" ? <p className="mb-3 rounded-lg bg-red-100 px-3 py-2 text-sm text-red-800">{error}</p> : null}
      <div className="fx-page" key={view === "form" ? `form-${draft?.id ?? "new"}` : "list"}>
        {view === "list" ? (
          <>
            <PageHeader
              title="Récurrentes"
              action={
                <Button
                  disabled={busy}
                  onClick={() => {
                    setError("");
                    setDraft(blank());
                    setView("form");
                  }}
                >
                  Nouvelle récurrence
                </Button>
              }
            />
            <QuotesListBar
              q={q}
              onQ={setQ}
              count={visible.length}
              unit="récurrences"
              filters={REC_FILTERS}
              filter={filter}
              onFilter={setFilter}
            />
            {visible.length === 0 ? (
              <div className="fx-clients-wrap">
                <p className="fx-clients-empty">{q.trim() || filter !== "all" ? "Aucune récurrence ne correspond." : "Aucune facture récurrente."}</p>
              </div>
            ) : (
              <div className="fx-clients-wrap">
                <table className="fx-clients-table">
                  <thead>
                    <tr>
                      <SortTh label="Statut" active={sort.key === "status"} dir={sort.dir} onSort={() => sort.onSort("status", "text")} />
                      <SortTh label="N°" active={sort.key === "number"} dir={sort.dir} onSort={() => sort.onSort("number", "text")} />
                      <SortTh label="Client" active={sort.key === "client"} dir={sort.dir} onSort={() => sort.onSort("client", "text")} />
                      <SortTh label="Fréquence" active={sort.key === "freq"} dir={sort.dir} onSort={() => sort.onSort("freq", "text")} />
                      <SortTh label="Début" active={sort.key === "start"} dir={sort.dir} onSort={() => sort.onSort("start", "date")} />
                      <SortTh label="Prochaine" active={sort.key === "next"} dir={sort.dir} onSort={() => sort.onSort("next", "date")} />
                      <SortTh label="Échéance" active={sort.key === "due"} dir={sort.dir} onSort={() => sort.onSort("due", "date")} />
                      <SortTh label="Cycles" active={sort.key === "cycles"} dir={sort.dir} onSort={() => sort.onSort("cycles", "num")} />
                      <SortTh className="is-num" label="TTC" active={sort.key === "ttc"} dir={sort.dir} onSort={() => sort.onSort("ttc", "num")} />
                      <th className="is-act">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paged.slice.map((r) => (
                      <tr key={r.id} className="fx-clients-table__row" onClick={() => void openEdit(r.id)}>
                        <td>
                          <span className={`fx-status is-${recStatusTone(r.status, r.waiting)}`}>{recStatusLabel(r.status, r.waiting)}</span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="fx-clients-table__name fx-clients-table__link"
                            onClick={(e) => {
                              e.stopPropagation();
                              void openEdit(r.id);
                            }}
                          >
                            {r.number || "—"}
                          </button>
                        </td>
                        <td>
                          {onOpenClient && r.client_id ? (
                            <button
                              type="button"
                              className="fx-clients-table__link"
                              onClick={(e) => {
                                e.stopPropagation();
                                onOpenClient(r.client_id);
                              }}
                            >
                              {r.client_name || "—"}
                            </button>
                          ) : (
                            r.client_name || "—"
                          )}
                        </td>
                        <td>{recFreqLabel(r.frequency)}</td>
                        <td className="fx-clients-table__muted">{day(r.starts_on)}</td>
                        <td className="fx-clients-table__muted">{r.status === "ended" ? "—" : day(r.next_run_on)}</td>
                        <td>{recDueDays(r.due_days) === 0 ? "À réception" : day(recDueOn(r))}</td>
                        <td>{recCyclesLabel(r.cycles_left)}</td>
                        <td className="is-num">{money(r.total_ttc_cents)}</td>
                        <td className="is-act" onClick={(e) => e.stopPropagation()}>
                          <RecurringMenu
                            status={r.status}
                            busy={busy}
                            open={menuId === r.id}
                            onOpenChange={(next) => setMenuId(next ? r.id : null)}
                            onEdit={() => void openEdit(r.id)}
                            onPdf={r.document_id ? () => setPdf({ id: r.document_id, number: r.number || null }) : undefined}
                            onCloneInvoice={
                              r.document_id
                                ? () => void cloneAsInvoice(r.document_id, recDueDays(r.due_days), r.payment_term_id)
                                : undefined
                            }
                            onDuplicate={() => void duplicateRecurring(r.id)}
                            onLaunch={() => void act(r.id, "launch")}
                            onPause={() => void act(r.id, "pause")}
                            onEnd={() => setConfirm({ kind: "end", id: r.id })}
                            onDelete={() => setConfirm({ kind: "del", id: r.id })}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <ListPager page={paged.page} pages={paged.pages} total={paged.total} from={paged.from} to={paged.to} setPage={paged.setPage} />
              </div>
            )}
          </>
        ) : null}
        {view === "form" && draft ? (
          <RecurringEditor
            companyId={companyId}
            draft={draft}
            setDraft={setDraft}
            clients={clients}
            rates={rates}
            decimalComma={decimalComma}
            defaultTaxId={defaultTaxId}
            busy={busy}
            error={error}
            onSave={() => void save()}
            onCancel={() => {
              setDraft(null);
              setView("list");
            }}
            onLaunch={draft.id ? () => void act(draft.id as number, "launch") : undefined}
            onPause={draft.id ? () => void act(draft.id as number, "pause") : undefined}
            onEnd={draft.id ? () => setConfirm({ kind: "end", id: draft.id as number }) : undefined}
            onDelete={draft.id ? () => setConfirm({ kind: "del", id: draft.id as number }) : undefined}
            onCloneInvoice={
              Number(draft.quote.id)
                ? () => void cloneAsInvoice(Number(draft.quote.id), draft.due_days, draft.payment_term_id)
                : undefined
            }
            onDuplicate={draft.id ? () => void duplicateRecurring(draft.id) : undefined}
          />
        ) : null}
      </div>
      {pdf ? (
        <QuotePdfDrawer
          src={quotePrintUrl(pdf.id)}
          onPrint={() => printQuotePdf(pdf.id, undefined, pdf.number || "Récurrence")}
          onClose={() => setPdf(null)}
        />
      ) : null}
      <ConfirmModal
        open={confirm !== null}
        title={confirm?.kind === "end" ? "Mettre fin à cette récurrence ?" : "Supprimer ce modèle ?"}
        message={
          confirm?.kind === "end"
            ? "Plus aucun brouillon ne sera créé. Les factures déjà émises restent."
            : "Le modèle jamais lancé sera effacé."
        }
        confirmVariant="danger"
        confirmLabel={confirm?.kind === "end" ? "Fin" : "Supprimer"}
        busy={busy}
        onCancel={() => {
          if (!busy) setConfirm(null);
        }}
        onConfirm={() => void applyConfirm()}
      />
    </div>
  );
}
